import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Badge, Box, CircularProgress, Tab, Tabs, Tooltip } from "@mui/material";
import React from "react";

import { TabPanel } from "components/common/TabPanel";
import useImageQueryPreview, { TabChoices } from "hooks/ImageQueryPreview/useImageQueryPreview";
import { Typography } from "new-components/typography/Typography";
import { Detector } from "types/detectors";

import { Activity } from "./Activity";
import { Flagged } from "./Flagged";
import { contentToolTipFlagged } from "./LineChartMetrics/ContentStrings";

export enum PageContext {
  Explore = "Explore",
  DetectorDetails = "DetectorDetails",
}

export enum ClickOrigin {
  Canvas = "canvas",
  AnswerBar = "answerBar",
}

interface PosicheckPreviewProps {
  detector: Detector | undefined;
  count: number;
  pageContext: PageContext;
  isActivityOnly?: boolean;
  columns?: number;
  areExamples?: boolean;
}

export function ImageQueryPreview(props: PosicheckPreviewProps) {
  const { detector, count, pageContext, isActivityOnly, columns = 3, areExamples } = props;

  const {
    detectorPageImageQueries,
    errorDetectorPageImageQueries,
    imageQueriesFlagged,
    errorImageQueriesFlagged,
    refetchImageQueriesFlagged,
    errorCountAnsweredImageQueries,
    errorMultichoiceAnsweredImageQueries,
    noTextImageQueries,
    errorNoTextImageQueries,
    hasTextImageQueries,
    errorHasTextImageQueries,
    unclearImageQueries,
    errorUnclearImageQueries,
    yesAnsweredImageQueries,
    errorYesAnsweredImageQueries,
    noAnsweredImageQueries,
    errorNoAnsweredImageQueries,
    hasBoxesImageQueries,
    errorHasBoxesImageQueries,
    noBoxesImageQueries,
    errorNoBoxesImageQueries,
    greaterThanMaxImageQueries,
    errorGreaterThanMaxImageQueries,
    relabeledPosicheck,
    selectedTab,
    choiceExamples,
    countExamples,
    flaggedCount,
    ordering,
    hasRelabeledPosicheck,
    isExplorePage,
    handleChange,
    handleChangeOrdering,
    onRefetch,
  } = useImageQueryPreview({
    detector,
    count,
    columns,
    areExamples: areExamples || false,
    pageContext,
  });

  const flaggedStatus = !imageQueriesFlagged || errorImageQueriesFlagged ? <CircularProgress size={25} /> : null;

  // If not request is loading and the counter is higher than 10 this componente will make
  // request again. This way server is not constanstly recieving request
  // Also we set the current state of the https request in the reducer for the other component
  const activity = (
    <Activity
      detector={detector}
      yesAnsweredImageQueries={yesAnsweredImageQueries}
      errorFlaggedImageQueries={errorUnclearImageQueries ? new Error(errorUnclearImageQueries.message) : undefined}
      errorNoImageQueries={errorNoAnsweredImageQueries ? new Error(errorNoAnsweredImageQueries.message) : undefined}
      errorYesImageQueries={errorYesAnsweredImageQueries ? new Error(errorYesAnsweredImageQueries.message) : undefined}
      imageQueriesFlagged={unclearImageQueries}
      noAnsweredImageQueries={noAnsweredImageQueries}
      onRefetch={onRefetch}
      pageContext={pageContext}
      detectorPageImageQueries={detectorPageImageQueries}
      errorExplorePageImageQueries={
        errorDetectorPageImageQueries ? new Error(errorDetectorPageImageQueries.message) : undefined
      }
      hasTextImageQueries={hasTextImageQueries}
      noTextImageQueries={noTextImageQueries}
      errorNoTextImageQueries={errorNoTextImageQueries ? new Error(errorNoTextImageQueries.message) : undefined}
      errorHasTextImageQueries={errorHasTextImageQueries ? new Error(errorHasTextImageQueries.message) : undefined}
      hasBoxesImageQueries={hasBoxesImageQueries}
      noBoxesImageQueries={noBoxesImageQueries}
      errorHasBoxesImageQueries={errorHasBoxesImageQueries ? new Error(errorHasBoxesImageQueries.message) : undefined}
      errorNoBoxesImageQueries={errorNoBoxesImageQueries ? new Error(errorNoBoxesImageQueries.message) : undefined}
      greaterThanMaxImageQueries={greaterThanMaxImageQueries}
      errorGreaterThanMaxImageQueries={
        errorGreaterThanMaxImageQueries ? new Error(errorGreaterThanMaxImageQueries.message) : undefined
      }
      multichoiceExamples={choiceExamples}
      errorMultichoiceExamples={errorMultichoiceAnsweredImageQueries}
      countExamples={countExamples}
      errorCountExamples={errorCountAnsweredImageQueries}
      columns={columns}
      isExamples={areExamples}
    />
  );

  if (isActivityOnly) {
    return activity;
  }

  return (
    <Box className="w-full">
      <Tabs
        value={selectedTab}
        onChange={handleChange}
        aria-label="ImageQuery Preview Tabs"
        variant="fullWidth"
        className="text-black"
      >
        <Tab
          label={<Typography variant="subtitle">{`${isExplorePage ? "Image Queries" : "Answer Examples"}`}</Typography>}
          value={TabChoices.Activity}
        />

        <Tab
          label={
            <Badge badgeContent={flaggedCount} color="error">
              <Tooltip title={contentToolTipFlagged}>
                <Box style={{ display: "flex", alignItems: "center" }}>
                  <InfoOutlinedIcon className="cursor-pointer" fontSize="small" />
                  <Typography variant="subtitle" className="ml-2">
                    Flagged
                  </Typography>
                </Box>
              </Tooltip>
            </Badge>
          }
          value={TabChoices.Flagged}
        />
      </Tabs>
      <TabPanel value={selectedTab} id={TabChoices.Activity}>
        {activity}
      </TabPanel>
      <TabPanel value={selectedTab} id={TabChoices.Flagged}>
        <Flagged
          detector={detector}
          getPosichecks={refetchImageQueriesFlagged}
          posichecks={imageQueriesFlagged}
          status={flaggedStatus}
          relabeledPosicheck={relabeledPosicheck}
          hasRelabeledPosicheck={hasRelabeledPosicheck}
          handleChangeOrdering={handleChangeOrdering}
          orderingParam={ordering}
        />
      </TabPanel>
    </Box>
  );
}
